/**
 * The drawn-shape catalogue: every `ShapeElement.shape` and its outline.
 *
 * Each outline is an SVG path in a UNIT BOX (x and y both 0..1, y down) —
 * the same convention `TextCurve.d` uses — so a renderer scales it to the
 * element box and nothing here knows about pixels. Hand-written where a
 * shape has one canonical look (heart, drop), generated where it is a
 * regular figure (polygons, stars, scallops), rounded to 4 decimals so the
 * strings are stable across machines.
 *
 * The list is closed on purpose: a shape id travels inside saved shopper
 * designs, so a renamed or removed entry would blank a layer on a paid
 * order. Add, never rename.
 */

export interface ShapeDef {
  /** Shopper-facing name (Serbian storefront). */
  labelSr: string;
  /** Studio label. */
  label: string;
  /** Unit-box SVG path (`M L H V C Q Z`, absolute). */
  d: string;
  /** Even-odd fill for shapes with a hole (ring). Nonzero otherwise. */
  fillRule?: 'evenodd';
  /** Natural aspect ratio (w / h) a fresh layer is created with. */
  aspect: number;
  /** Where the tile sits in the shopper's picker. */
  group: 'basic' | 'love' | 'fun' | 'labels';
}

const K = 0.5523; // circle-from-cubics constant

const n4 = (v: number): string => String(Math.round(v * 10000) / 10000);
const pt = (x: number, y: number): string => `${n4(x)} ${n4(y)}`;

/** A regular polygon inscribed in the unit box, first vertex at 12 o'clock. */
function polygon(sides: number, rotationDeg = 0): string {
  const points: Array<[number, number]> = [];
  for (let i = 0; i < sides; i++) {
    const a = ((-90 + rotationDeg + (360 / sides) * i) * Math.PI) / 180;
    points.push([0.5 + 0.5 * Math.cos(a), 0.5 + 0.5 * Math.sin(a)]);
  }
  return normalise(points);
}

/** A star with `points` tips; `inner` is the inner radius as a fraction of the outer. */
function star(points: number, inner: number, rotationDeg = 0): string {
  const out: Array<[number, number]> = [];
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? 0.5 : 0.5 * inner;
    const a = ((-90 + rotationDeg + (180 / points) * i) * Math.PI) / 180;
    out.push([0.5 + r * Math.cos(a), 0.5 + r * Math.sin(a)]);
  }
  return normalise(out);
}

/**
 * Fits a point list into the unit box (a polygon's vertices do not touch all
 * four edges) and writes it as a closed path.
 */
function normalise(points: Array<[number, number]>): string {
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const sx = 1 / (maxX - minX), sy = 1 / (maxY - minY);
  return (
    points
      .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${pt((x - minX) * sx, (y - minY) * sy)}`)
      .join(' ') + ' Z'
  );
}

/** A circle of `n` bumps — a rosette / badge / flower depending on depth. */
function scallop(n: number, depth: number): string {
  const parts: string[] = [];
  const rIn = 0.5 - depth;
  for (let i = 0; i < n; i++) {
    const a0 = ((-90 + (360 / n) * i) * Math.PI) / 180;
    const a1 = ((-90 + (360 / n) * (i + 1)) * Math.PI) / 180;
    const am = (a0 + a1) / 2;
    const x0 = 0.5 + rIn * Math.cos(a0), y0 = 0.5 + rIn * Math.sin(a0);
    const x1 = 0.5 + rIn * Math.cos(a1), y1 = 0.5 + rIn * Math.sin(a1);
    // Control point pushed out past the rim so the bump reaches radius 0.5.
    const cr = 0.5 + depth * 1.15;
    const cx = 0.5 + cr * Math.cos(am), cy = 0.5 + cr * Math.sin(am);
    parts.push(i === 0 ? `M${pt(x0, y0)}` : '');
    parts.push(`Q${pt(cx, cy)} ${pt(x1, y1)}`);
  }
  return parts.filter(Boolean).join(' ') + ' Z';
}

const ELLIPSE = `M0.5 0 C${n4(0.5 + 0.5 * K)} 0 1 ${n4(0.5 - 0.5 * K)} 1 0.5 C1 ${n4(0.5 + 0.5 * K)} ${n4(0.5 + 0.5 * K)} 1 0.5 1 C${n4(0.5 - 0.5 * K)} 1 0 ${n4(0.5 + 0.5 * K)} 0 0.5 C0 ${n4(0.5 - 0.5 * K)} ${n4(0.5 - 0.5 * K)} 0 0.5 0 Z`;

function circleAt(cx: number, cy: number, r: number): string {
  const k = K * r;
  return `M${pt(cx, cy - r)} C${pt(cx + k, cy - r)} ${pt(cx + r, cy - k)} ${pt(cx + r, cy)} C${pt(cx + r, cy + k)} ${pt(cx + k, cy + r)} ${pt(cx, cy + r)} C${pt(cx - k, cy + r)} ${pt(cx - r, cy + k)} ${pt(cx - r, cy)} C${pt(cx - r, cy - k)} ${pt(cx - k, cy - r)} ${pt(cx, cy - r)} Z`;
}

export const SHAPES = {
  rect: { labelSr: 'Pravougaonik', label: 'Rectangle', d: 'M0 0 H1 V1 H0 Z', aspect: 1.4, group: 'basic' },
  roundRect: {
    labelSr: 'Zaobljeni pravougaonik', label: 'Rounded rectangle', aspect: 1.4, group: 'basic',
    d: 'M0.12 0 H0.88 Q1 0 1 0.12 V0.88 Q1 1 0.88 1 H0.12 Q0 1 0 0.88 V0.12 Q0 0 0.12 0 Z',
  },
  ellipse: { labelSr: 'Krug', label: 'Ellipse', d: ELLIPSE, aspect: 1, group: 'basic' },
  triangle: { labelSr: 'Trougao', label: 'Triangle', d: 'M0.5 0 L1 1 H0 Z', aspect: 1.1, group: 'basic' },
  diamond: { labelSr: 'Romb', label: 'Diamond', d: 'M0.5 0 L1 0.5 L0.5 1 L0 0.5 Z', aspect: 1, group: 'basic' },
  pentagon: { labelSr: 'Petougao', label: 'Pentagon', d: polygon(5), aspect: 1.05, group: 'basic' },
  hexagon: { labelSr: 'Šestougao', label: 'Hexagon', d: polygon(6, 30), aspect: 1.15, group: 'basic' },
  octagon: { labelSr: 'Osmougao', label: 'Octagon', d: polygon(8, 22.5), aspect: 1, group: 'basic' },
  ring: {
    labelSr: 'Prsten', label: 'Ring', aspect: 1, group: 'basic', fillRule: 'evenodd',
    d: `${circleAt(0.5, 0.5, 0.5)} ${circleAt(0.5, 0.5, 0.32)}`,
  },
  plus: {
    labelSr: 'Plus', label: 'Plus', aspect: 1, group: 'basic',
    d: 'M0.35 0 H0.65 V0.35 H1 V0.65 H0.65 V1 H0.35 V0.65 H0 V0.35 H0.35 Z',
  },

  heart: {
    labelSr: 'Srce', label: 'Heart', aspect: 1.08, group: 'love',
    d: 'M0.5 1 C0.5 1 0 0.66 0 0.32 C0 0.13 0.15 0 0.3 0 C0.4 0 0.47 0.06 0.5 0.15 C0.53 0.06 0.6 0 0.7 0 C0.85 0 1 0.13 1 0.32 C1 0.66 0.5 1 0.5 1 Z',
  },
  heartRound: {
    labelSr: 'Puno srce', label: 'Round heart', aspect: 1, group: 'love',
    d: 'M0.5 1 C0.42 0.93 0 0.7 0 0.36 C0 0.14 0.17 0 0.32 0 C0.41 0 0.47 0.05 0.5 0.12 C0.53 0.05 0.59 0 0.68 0 C0.83 0 1 0.14 1 0.36 C1 0.7 0.58 0.93 0.5 1 Z',
  },
  heartOutline: {
    labelSr: 'Srce (okvir)', label: 'Heart outline', aspect: 1.08, group: 'love', fillRule: 'evenodd',
    d: 'M0.5 1 C0.5 1 0 0.66 0 0.32 C0 0.13 0.15 0 0.3 0 C0.4 0 0.47 0.06 0.5 0.15 C0.53 0.06 0.6 0 0.7 0 C0.85 0 1 0.13 1 0.32 C1 0.66 0.5 1 0.5 1 Z M0.5 0.84 C0.62 0.74 0.88 0.55 0.88 0.33 C0.88 0.2 0.8 0.12 0.7 0.12 C0.6 0.12 0.53 0.2 0.5 0.3 C0.47 0.2 0.4 0.12 0.3 0.12 C0.2 0.12 0.12 0.2 0.12 0.33 C0.12 0.55 0.38 0.74 0.5 0.84 Z',
  },
  star5: { labelSr: 'Zvezda', label: 'Star', d: star(5, 0.382), aspect: 1.05, group: 'love' },
  star4: { labelSr: 'Iskra', label: 'Sparkle', d: star(4, 0.28), aspect: 1, group: 'love' },
  star6: { labelSr: 'Zvezda 6', label: 'Star 6', d: star(6, 0.55), aspect: 1, group: 'love' },
  flower: { labelSr: 'Cvet', label: 'Flower', d: scallop(6, 0.16), aspect: 1, group: 'love' },
  flower8: { labelSr: 'Cvet 8', label: 'Flower 8', d: scallop(8, 0.12), aspect: 1, group: 'love' },
  drop: {
    labelSr: 'Kap', label: 'Drop', aspect: 0.75, group: 'love',
    d: 'M0.5 0 C0.5 0 1 0.45 1 0.68 C1 0.86 0.78 1 0.5 1 C0.22 1 0 0.86 0 0.68 C0 0.45 0.5 0 0.5 0 Z',
  },
  crescent: {
    labelSr: 'Mesec', label: 'Crescent', aspect: 0.92, group: 'fun',
    d: 'M0.6 0 C0.27 0 0 0.22 0 0.5 C0 0.78 0.27 1 0.6 1 C0.75 1 0.89 0.95 1 0.87 C0.62 0.84 0.34 0.69 0.34 0.5 C0.34 0.31 0.62 0.16 1 0.13 C0.89 0.05 0.75 0 0.6 0 Z',
  },
  cloud: {
    labelSr: 'Oblak', label: 'Cloud', aspect: 1.5, group: 'fun',
    d: 'M0.25 1 C0.11 1 0 0.9 0 0.77 C0 0.66 0.08 0.57 0.19 0.55 C0.17 0.5 0.16 0.45 0.16 0.4 C0.16 0.24 0.29 0.12 0.44 0.12 C0.5 0.12 0.55 0.14 0.6 0.17 C0.66 0.06 0.77 0 0.86 0 C0.94 0 1 0.09 1 0.2 C1 0.24 0.99 0.28 0.97 0.31 C0.99 0.36 1 0.41 1 0.47 C1 0.55 0.97 0.62 0.92 0.67 C0.98 0.7 1 0.76 1 0.82 C1 0.92 0.91 1 0.8 1 Z',
  },
  lightning: {
    labelSr: 'Munja', label: 'Lightning', aspect: 0.7, group: 'fun',
    d: 'M0.55 0 L0.15 0.55 H0.42 L0.3 1 L0.85 0.4 H0.58 L0.75 0 Z',
  },
  crown: {
    labelSr: 'Kruna', label: 'Crown', aspect: 1.35, group: 'fun',
    d: 'M0 1 V0.3 L0.27 0.55 L0.5 0.08 L0.73 0.55 L1 0.3 V1 Z',
  },
  burst: { labelSr: 'Prasak', label: 'Burst', d: star(12, 0.78), aspect: 1, group: 'fun' },
  badge: { labelSr: 'Bedž', label: 'Badge', d: scallop(14, 0.07), aspect: 1, group: 'labels' },
  speech: {
    labelSr: 'Oblačić', label: 'Speech bubble', aspect: 1.3, group: 'labels',
    d: 'M0.1 0 H0.9 Q1 0 1 0.1 V0.66 Q1 0.76 0.9 0.76 H0.42 L0.2 1 L0.25 0.76 H0.1 Q0 0.76 0 0.66 V0.1 Q0 0 0.1 0 Z',
  },
  speechRound: {
    labelSr: 'Okrugli oblačić', label: 'Round speech bubble', aspect: 1.3, group: 'labels',
    d: 'M0.5 0 C0.78 0 1 0.17 1 0.39 C1 0.6 0.78 0.78 0.5 0.78 C0.44 0.78 0.38 0.77 0.33 0.75 L0.14 1 L0.2 0.72 C0.08 0.65 0 0.53 0 0.39 C0 0.17 0.22 0 0.5 0 Z',
  },
  banner: {
    labelSr: 'Traka', label: 'Banner', aspect: 3, group: 'labels',
    d: 'M0 0 H1 L0.9 0.5 L1 1 H0 L0.1 0.5 Z',
  },
  ribbon: {
    labelSr: 'Mašna traka', label: 'Ribbon', aspect: 3.2, group: 'labels',
    d: 'M0.12 0.2 H0.88 V0.8 H0.12 Z M0 0.35 H0.12 V0.95 L0.06 0.85 L0 0.95 Z M0.88 0.35 H1 V0.95 L0.94 0.85 L0.88 0.95 Z',
  },
  arrowRight: {
    labelSr: 'Strelica', label: 'Arrow', aspect: 1.6, group: 'labels',
    d: 'M0 0.3 H0.6 V0.05 L1 0.5 L0.6 0.95 V0.7 H0 Z',
  },
  arrowDouble: {
    labelSr: 'Dvostruka strelica', label: 'Double arrow', aspect: 2, group: 'labels',
    d: 'M0.25 0.05 L0 0.5 L0.25 0.95 V0.7 H0.75 V0.95 L1 0.5 L0.75 0.05 V0.3 H0.25 Z',
  },
  tag: {
    labelSr: 'Etiketa', label: 'Tag', aspect: 1.7, group: 'labels', fillRule: 'evenodd',
    d: `M0 0.5 L0.25 0 H1 V1 H0.25 Z ${circleAt(0.22, 0.5, 0.07)}`,
  },
} as const satisfies Record<string, ShapeDef>;

export type ShapeKind = keyof typeof SHAPES;

export const SHAPE_KINDS = Object.keys(SHAPES) as ShapeKind[];

export const SHAPE_GROUPS: Array<{ id: ShapeDef['group']; labelSr: string; label: string }> = [
  { id: 'basic', labelSr: 'Osnovni', label: 'Basic' },
  { id: 'love', labelSr: 'Srca i zvezde', label: 'Hearts & stars' },
  { id: 'fun', labelSr: 'Zabavni', label: 'Fun' },
  { id: 'labels', labelSr: 'Etikete', label: 'Labels' },
];

export function isShapeKind(value: unknown): value is ShapeKind {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(SHAPES, value);
}

/** The unit-box outline of a shape; unknown kinds fall back to a rectangle. */
export function shapePath(kind: string): string {
  return (SHAPES as Record<string, ShapeDef>)[kind]?.d ?? SHAPES.rect.d;
}

export function shapeDef(kind: string): ShapeDef {
  return (SHAPES as Record<string, ShapeDef>)[kind] ?? SHAPES.rect;
}

/**
 * The shape as a standalone SVG document (unit box scaled to `size`), for
 * pickers and thumbnails. `fill`/`stroke` are CSS colours; the stroke width
 * is in the same units as `size`.
 */
export function shapeSvg(
  kind: string,
  options: { size?: number; fill?: string; stroke?: string; strokeWidth?: number } = {},
): string {
  const { size = 48, fill = 'currentColor', stroke, strokeWidth = 0 } = options;
  const def = shapeDef(kind);
  const w = size, h = size / def.aspect;
  const rule = def.fillRule ? ` fill-rule="${def.fillRule}"` : '';
  const strokeAttrs = stroke && strokeWidth > 0
    ? ` stroke="${stroke}" stroke-width="${n4(strokeWidth)}" stroke-linejoin="round" vector-effect="non-scaling-stroke"`
    : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${n4(w)} ${n4(h)}" width="${n4(w)}" height="${n4(h)}"><path transform="scale(${n4(w)} ${n4(h)})" d="${def.d}" fill="${fill}"${rule}${strokeAttrs}/></svg>`;
}
